import { uniqueId } from 'lodash';
import {
  DesignRoomDetailsResponse,
  mapDesignRoomDetailsResponseToFormModel,
} from '../../designs/designRoomDetailsResponse';
import { BuildTypeCode } from '../../projects/project';
import { RoomlessManifoldDetailsResponse } from '../manifolds/manifoldResponse';
import {
  DataForQuoteOrDesignForm,
  getFloorConstructionOrNull,
} from '../quote-or-design-form/GetDataForQuoteOrDesignForm';
import { FloorLevelCode, ThermostatTypeCode } from '../quote-or-design-form/quoteOrDesignFormData';
import { getInitialDesignRoomDetailsFormModel } from '../quote-or-design-form/rooms/designRoomDetailsFormModel';
import { RoomFormModel } from '../quote-or-design-form/rooms/roomFormModel';
import { getRoomTypeOrNull } from '../quote-or-design-form/rooms/roomTypes';
import {
  mapRoomHeatLossDetailsResponseToFormModel,
  RoomHeatLossDetailsResponse,
} from './heatLoss/roomHeatLossDetailsResponse';

export type RoomResponse = {
  roomId: number;
  name: string;
  roomType: string | null;
  displayIndex: number;
  roomTemperatureInDegreesCelsius: number | null;
  floorLevelCode: FloorLevelCode;
  floorConstructionPartId: number | null;
  areaInMetresSquared: number | null;
  thermostatTypeCode: ThermostatTypeCode;
  thermostatSharedWithRoomId: number | null;
  numberOfThermostats: number | null;
  numberOfWetSensors: number | null;
  numberOfFloorSensors: number | null;
  numberOfPorts: number | null;
  skirtingHeating: RoomSkirtingHeatingDetailsResponse | null;
  heatLossDetails: RoomHeatLossDetailsResponse | null;
  designRoomDetails: DesignRoomDetailsResponse | null;
};

export type RoomSkirtingHeatingDetailsResponse = {
  perimeterInMetres: number;
};

export const mapRoomResponseToFormModel = (
  roomResponse: RoomResponse,
  data: DataForQuoteOrDesignForm,
  createDesignDetails: boolean,
  buildTypeCode: BuildTypeCode,
): RoomFormModel => {
  const roomType = roomResponse.roomType == null ? getRoomTypeOrNull(roomResponse.name) : roomResponse.roomType;
  const floorConstruction = getFloorConstructionOrNull(roomResponse.floorConstructionPartId, data);

  return {
    roomId: roomResponse.roomId,
    id: uniqueId('room'),
    roomType,
    name: roomResponse.name,
    roomTemperature: roomResponse.roomTemperatureInDegreesCelsius?.toString() ?? '',
    floorLevelCode: roomResponse.floorLevelCode,
    floorConstruction,
    areaInMetresSquared: roomResponse.areaInMetresSquared?.toString() ?? '',
    thermostatTypeCode: roomResponse.thermostatTypeCode,
    thermostatSharedWithId: null, //this will be set on the manifold level based on thermostatSharedWithRoomId
    numberOfThermostats: roomResponse.numberOfThermostats?.toString() ?? '',
    numberOfWetSensors: roomResponse.numberOfWetSensors?.toString() ?? '',
    numberOfFloorSensors: roomResponse.numberOfFloorSensors?.toString() ?? '',
    numberOfPorts: roomResponse.numberOfPorts?.toString() ?? '',
    perimeterInMetres: roomResponse.skirtingHeating?.perimeterInMetres?.toString() ?? '',
    heatLossDetails:
      roomResponse.heatLossDetails == null
        ? null
        : mapRoomHeatLossDetailsResponseToFormModel(roomResponse.heatLossDetails),
    designRoomDetails:
      roomResponse.designRoomDetails == null
        ? createDesignDetails
          ? getInitialDesignRoomDetailsFormModel(buildTypeCode, floorConstruction, roomType)
          : null
        : mapDesignRoomDetailsResponseToFormModel(roomResponse.designRoomDetails),
  };
};

export const mapRoomlessManifoldDetailsResponseToRoomFormModel = (
  roomlessManifoldDetailsResponse: RoomlessManifoldDetailsResponse,
  parts: DataForQuoteOrDesignForm,
  buildTypeCode: BuildTypeCode,
): RoomFormModel => ({
  roomId: null,
  id: uniqueId('room'),
  roomType: null,
  name: 'Copied from Manifold',
  roomTemperature: '',
  floorLevelCode: roomlessManifoldDetailsResponse.floorLevelCode,
  floorConstruction: getFloorConstructionOrNull(roomlessManifoldDetailsResponse.floorConstructionPartId, parts),
  areaInMetresSquared: roomlessManifoldDetailsResponse.areaInMetresSquared?.toString() ?? '',
  thermostatTypeCode: roomlessManifoldDetailsResponse.thermostatTypeCode,
  thermostatSharedWithId: null,
  numberOfThermostats: roomlessManifoldDetailsResponse.numberOfThermostats?.toString() ?? '',
  numberOfWetSensors: roomlessManifoldDetailsResponse.numberOfWetSensors?.toString() ?? '',
  numberOfFloorSensors: roomlessManifoldDetailsResponse.numberOfFloorSensors?.toString() ?? '',
  numberOfPorts: roomlessManifoldDetailsResponse.numberOfPorts?.toString() ?? '',
  perimeterInMetres: '',
  heatLossDetails: null,
  designRoomDetails: getInitialDesignRoomDetailsFormModel(
    buildTypeCode,
    getFloorConstructionOrNull(roomlessManifoldDetailsResponse.floorConstructionPartId, parts),
    null,
  ),
});
